import Vue from 'vue';
import mixins from 'vue-typed-mixins';
import moment, { Moment } from 'moment';
import _ from 'lodash';

import {
  CourseProgress,
  StudyRequest,
} from '@/openapi/user';
import {
  CourseId,
  ResultNo,
} from '@/openapi/enum';
import { QuestionApiAxiosParamCreator } from '@/openapi/master';

import RoutesMixin from '@/user/mixins/RoutesMixin';
import UserMixin from '@/user/mixins/UserMixin';
import UserApiUtility from '@/classes/common/UserApiUtility';
import MasterApiUtility from '@/classes/common/MasterApiUtility';
import QuestionData from '@/classes/data/QuestionData';

export enum STUDY_RESULT {
  SCORE_SUCCESS,     // 採点で問題正解
  SCORE_EMPTY,       // 採点で未回答問題あり
  SCORE_RETRY,       // 採点で不正解だけどおてつき許容範囲内
  SCORE_FAIL,        // 採点で不正解でおてつき許容範囲外
  UNIT_SUCCESS,      // ユニット内全問正解
  UNIT_FAIL,         // ユニット内に不正解あり
  UNIT_TIMEUP,       // ユニット制限時間超え
  COURSE_COMPLETE,   // ユニット内全問正解してコース内全ユニット正解
};

type State = {
  questions: QuestionData[];
  questionIndex: number;
  questionMediaCount: number;
  studyStartMoment: Moment;
  studyRequest: StudyRequest;
  incorrectLimit: number;
  incorrectCount: number;
  questionStartTime: number;
  questionRestMs: number;
  restTimeInterval: number | null;
  scoreResult: number | null;  // 結果モーダルを閉じた後の処理分岐用
  studyResult: number | null;
  studyResultTimeout: number | null;
  isFontLoaded: boolean;
  limitSec: number;
  hasUnitSec: boolean;
};

const state = Vue.observable<State>({
  questions: [],
  questionIndex: -1,
  questionMediaCount: 0,
  studyStartMoment: moment(),
  studyRequest: {
    courseId: CourseId.calc,
    sectionNo: 0,
    unitNo: 0,
    stageName: '',
    resultNo: ResultNo.fail,
    studyMemo: '',
    studyStartDt: moment().format(),
    studySec: 0,
  },
  incorrectLimit: 0,
  incorrectCount: 0,
  questionStartTime: 0,
  questionRestMs: 0,
  restTimeInterval: null,
  scoreResult: null,
  studyResult: null,
  studyResultTimeout: null,
  isFontLoaded: false,
  limitSec: 0,
  hasUnitSec: false,
});




export default mixins(RoutesMixin, UserMixin).extend({

  name: 'StudyMixin',

  data(): {
    studyCourseId: CourseId;
    studySectionNo: number;
    studyUnitNo: number;
    tutorialSectionNo: number;
  } {
    return {
      studyCourseId: this.$route.params.courseId as CourseId,
      studySectionNo: Number(this.$route.query.sectionNo),
      studyUnitNo: Number(this.$route.query.unitNo),
      tutorialSectionNo: 0
    };
  },

  computed: {
    isTutorial(): boolean {
      return this.studySectionNo === this.tutorialSectionNo;
    },
    questions(): QuestionData[] {
      return state.questions;
    },
    question(): QuestionData | null {
      const question = state.questions[state.questionIndex];
      return question !== undefined ? question : null;
    },
    isQuestionsLoaded(): boolean {
      return state.questionIndex >= 0;
    },
    is1stQuestion(): boolean {
      return state.questionIndex === 0;
    },
    isQuestionLoadCompelete(): boolean {
      return this.isQuestionsLoaded && state.questionMediaCount === 0;
    },
    hasNextQuestion(): boolean {
      return this.questions.length > state.questionIndex + 1;
    },
    stageName(): string {
      return state.studyRequest.stageName;
    },
    questionIndex(): number {
      return state.questionIndex;
    },
    questionCount(): number {
      return this.questions.length;
    },
    questionRestMs(): number {
      return state.questionRestMs;
    },
    incorrectLimit(): number {
      return state.incorrectLimit;
    },
    incorrectCount(): number {
      return state.incorrectCount;
    },
    studyResult(): number | null {
      return state.studyResult;
    },
    scoreResult(): number | null {
      return state.scoreResult;
    },
  },

  methods: {
    initializeStudyVariable(): void {
      this.clearRestTimeInterval();
      this.clearStudyVariable();

      this.studyCourseId  = this.$route.params.courseId as CourseId; // 初期化不要だが、一律で初期化しておく
      this.studySectionNo = Number(this.$route.query.sectionNo);
      this.studyUnitNo    = Number(this.$route.query.unitNo);
    },

    initializeStudyRequest(): void {
      // 学習結果初期化
      state.studyStartMoment = moment();
      state.studyRequest = {
        courseId:     this.studyCourseId,
        sectionNo:    this.studySectionNo,
        unitNo:       this.studyUnitNo,
        stageName:    '',
        resultNo:     ResultNo.fail,
        studyMemo:    '',
        studyStartDt: state.studyStartMoment.format(),
        studySec:     0,
      };
    },

    // 各コースcomponentのcreated先頭で実行すること
    async loadFont(): Promise<void> {
      // 既にfontの読み込みが完了している場合は終了
      if (state.isFontLoaded) return;

      let checkCount = 0;
      const checkFontLoaded = async (): Promise<void> => {
        if (!this.$_isLoaded() && checkCount <= 30) {
          checkCount++;
          await new Promise(resolve => setTimeout(resolve, 100));
          await checkFontLoaded();
        }
      };
      // フォントが読み込まれたかチェックし、完了したらisFontLoadedをtrueにして終了
      await checkFontLoaded();
      state.isFontLoaded = true;
    },

    async loadQuestions(): Promise<void> {
      this.initializeQuestionIndex();
      const questions = await MasterApiUtility.getQuestions(
        this.studyCourseId,
        this.studySectionNo
      );

      state.questions = this.$_extractUnitQuestions(questions);
    },

    async loadJudgeQuestions(): Promise<void> {
      this.initializeQuestionIndex();
      const questions = await MasterApiUtility.getQuestions(
        this.studyCourseId,
        this.studySectionNo
      );
      state.questions = this.$_extractJudgeQuestions(questions);
    },

    setQuestions(questions: QuestionData[]): void {
      state.questions = questions;
    },

    initializeQuestionIndex(): void {
      state.questionIndex = -1;
    },

    forwardQuestionIndex(): void {
      state.questionIndex++;
    },

    // 各コースcomponentで上書き想定
    nextQuestion(): void {
      this.forwardQuestionIndex();
      this.startTimer();
    },

    setStageName(stageName: string): void {
      state.studyRequest.stageName = stageName;
    },

    startTimer(unitSec?: number): void {
      // 読み込み完了前は終了
      if (!this.isQuestionLoadCompelete) return;
      if (this.question === null) throw new Error();

      // 初問は学習開始日時を記録
      if (this.is1stQuestion) state.studyStartMoment = moment();
      state.questionStartTime = moment().valueOf();

      const checkRestTime = () => {
        // 学習が終了している場合はタイマーストップ
        if (state.studyResult !== null && state.studyResult >= STUDY_RESULT.UNIT_SUCCESS) {
          this.clearRestTimeInterval();
        }

        state.hasUnitSec = unitSec !== undefined;
        let standardSec  = unitSec !== undefined ? unitSec : this.question!.standardSec;
        // TODO:マスタ改修までの暫定処理（計算と読解は制限時間２倍）
        switch (this.studyCourseId) {
          case CourseId.calc:
          case CourseId.japanese:
            standardSec = standardSec * 2;
            break;
          default:
            break;
        }
        state.limitSec       = standardSec;
        state.questionRestMs = standardSec * 1000 - (moment().valueOf() - state.questionStartTime);

        // ダイアログ表示中または残り時間がある場合はreturn
        if (state.studyResult !== null || state.questionRestMs > 0) return;

        // チュートリアルは時間切れ無し
        if (!this.isTutorial) {
          this.addStudyMemo(this.timeupMemo());
          this.$_setScoreResult(STUDY_RESULT.UNIT_TIMEUP);
        }
        this.clearRestTimeInterval();
      };
      if (state.restTimeInterval === null) state.restTimeInterval = setInterval(checkRestTime, 100);
    },

    // 少なくとも計算では上書き想定
    timeupMemo(): string {
      return 'TIMEUP';
    },

    clearRestTimeInterval(): void {
      if (state.restTimeInterval === null) return;

      clearInterval(state.restTimeInterval);
      state.restTimeInterval = null;
    },

    // 問題にimage/video/audioが指定されている場合に増
    incrementMediaCount(): void {
      state.questionMediaCount++;
    },

    // image/video/audioの読み込み完了時に呼ばれる
    loadMedia(isInputClear = false): void {
      state.questionMediaCount--;
      if(!isInputClear) this.startTimer();
    },

    setIncorretLimit(incorrectLimit: number): void {
      state.incorrectLimit = incorrectLimit;
    },

    incrementIncorretCount(): void {
      state.incorrectCount++;
    },

    endupIncorretLimit(): void {
      state.incorrectCount = state.incorrectLimit;
    },

    addStudyMemo(memo: string): void {
      if (state.studyRequest.studyMemo.length > 0) state.studyRequest.studyMemo += ',';
      state.studyRequest.studyMemo += memo;
    },

    judgeScoreResult(hasEmptyReplys: boolean, isCorrect: boolean, incorrectLimit: number): void {
      let scoreResult = STUDY_RESULT.SCORE_FAIL;
      if (hasEmptyReplys)
        scoreResult = STUDY_RESULT.SCORE_EMPTY;
      else if (isCorrect)
        scoreResult = STUDY_RESULT.SCORE_SUCCESS;
      else if (this.isTutorial || this.incorrectCount < incorrectLimit)
        scoreResult = STUDY_RESULT.SCORE_RETRY;

      this.$_setScoreResult(scoreResult);
    },

    clearStudyVariable(): void {
      state.questions = [];
      state.questionIndex = -1;
      state.questionMediaCount = 0;
      state.incorrectLimit = 0;
      state.incorrectCount = 0;
      state.questionStartTime = 0;
      state.questionRestMs = 0;
      state.scoreResult = null;
      state.studyResult = null;
      state.studyResultTimeout = null;
      state.limitSec = 0;
      state.hasUnitSec = false;
    },

    clearStudyResult(): void {
      state.studyResult = null;
      if (state.studyResultTimeout !== null) {
        clearTimeout(state.studyResultTimeout);
        state.studyResultTimeout = null;
      }
    },

    // section/unit 終了時に呼ぶ想定
    setStudyResult(studyResult: STUDY_RESULT): void {
      state.studyResult = studyResult;
    },

    // 結果モーダル自動閉時の処理
    // 計算、読解はこのまま使えるが他コースcomponentで上書きも想定
    afterScore(): void {
      switch (this.scoreResult) {
        case STUDY_RESULT.SCORE_SUCCESS:
          if (this.hasNextQuestion) {
            this.nextQuestion();
          } else {
            this.saveStudyResult(true);
          }
          break;
        case STUDY_RESULT.SCORE_FAIL:
        case STUDY_RESULT.UNIT_TIMEUP:
          this.saveStudyResult(false);
          break;
      }
    },

    // 必要であれば各コースcomponentで上書き想定
    beforeSaveStudyResult(): void {
    },

    // 計算、読解はこのまま使えるが他コースcomponentで上書きも想定
    saveStudyResult(isCorrect: boolean): void {

      this.beforeSaveStudyResult();

      let studyResult = STUDY_RESULT.UNIT_SUCCESS;
      // progressの更新
      this.updateProgress(isCorrect);

      // チュートリアルの場合
      if (this.isTutorial) {
        // 何もしない

      // 全ユニット終了した場合
      } else if (this.isCourseComplete(isCorrect)) {
        studyResult = STUDY_RESULT.COURSE_COMPLETE;

      // 全ユニット終了していない、かつ不正解の場合
      } else if (!isCorrect) {
        studyResult = STUDY_RESULT.UNIT_FAIL;
      }

      this.setStudyResult(studyResult);
      if (!this.isGuest) this.$_sendStudyResult(isCorrect); // 体験モード以外は結果を送信する
    },

    // 計算、読解はこのまま使えるが他コースcomponentで上書きも想定
    clickResultModalToMenu(): void {
      this.$router.push({
        name:   this.routes.UnitSelect.name,
        params: this.$route.params
      });
      this.clearStudyResult();
    },

    // 各コースcomponentで上書き想定
    isCourseComplete(isCorrect: boolean): boolean {
      return false;
    },

    // 各コースcomponentで上書き想定
    updateProgress(isCorrect: boolean): void {
    },

    // 各コースcomponentで上書き想定
    getProgress(): CourseProgress {
      return {
        sectionNo: this.studySectionNo,
        unitNos: [],
      };
    },

    // 各コースcomponentで上書き想定
    initializeCourseComponent(): void {
    },

    calcStudySec(scoreResult: STUDY_RESULT): void {
      switch (scoreResult) {
        case STUDY_RESULT.SCORE_SUCCESS:
        case STUDY_RESULT.SCORE_FAIL:
          state.studyRequest.studySec = Math.round((moment().valueOf() - state.studyStartMoment.valueOf()) / 1000);
          break;
        case STUDY_RESULT.UNIT_TIMEUP:
          if (state.hasUnitSec) state.studyRequest.studySec = state.limitSec;
          else                  state.studyRequest.studySec += state.limitSec;
        default:
          break;
      }
    },

    $_extractJudgeQuestions(questions: QuestionData[]): QuestionData[] {
      const ret: QuestionData[] = [];

      // 判定問題絞り込み
      const judgeQuestions = _.filter(questions, q => 0 < q.judgeQuestionNo);
      for (let i = 1; ; i++) {
        if (judgeQuestions.length === 0) break;

        const tmpQuestions = _.filter(judgeQuestions, q => q.judgeQuestionNo === i);
        if      (tmpQuestions.length === 0) continue;
        else if (tmpQuestions.length === 1) ret.push(tmpQuestions[0]);
        else                                ret.push(tmpQuestions[_.random(0, tmpQuestions.length - 1)]);

        _.remove(judgeQuestions, q => q.judgeQuestionNo === i); // filterかけた問題を削除
      }

      return ret;
    },

    $_extractUnitQuestions(questions: QuestionData[]): QuestionData[] {
      // 対象ユニットの問題
      const unitNo        = this.studyUnitNo;
      const unitQuestions = _.filter(questions, q => q.unitNo === unitNo);

      // 復習問題の取得
      const repeatQuestions: QuestionData[] = [];
      if (unitNo > 1) {
        // 前のユニットの復習問題
        const prevUnitQuestions = _.filter(questions, q => q.unitNo === unitNo - 1 && q.repeatQuestionNo > 0);
        for (let i = 1; ; i++) {
          if (prevUnitQuestions.length === 0) break;

          const tmpQuestions = _.filter(prevUnitQuestions, q => q.repeatQuestionNo === i);
          if      (tmpQuestions.length === 0) continue;
          else if (tmpQuestions.length === 1) repeatQuestions.push(tmpQuestions[0]);
          else                                repeatQuestions.push(tmpQuestions[_.random(0, tmpQuestions.length - 1)]);

          _.remove(prevUnitQuestions, q => q.repeatQuestionNo === i); // filterかけた問題を削除
        }
      }

      return _.concat(repeatQuestions, unitQuestions);
    },

    $_getQuestionImageUrl(sectionCode: string, questionFile: string): string {
      const basePath = MasterApiUtility.getApiBasePath();
      const creator  = QuestionApiAxiosParamCreator;
      const apiPath  = creator().showQuestion(this.studyCourseId, sectionCode, questionFile).url;
      return `<${location.protocol}//${location.host}${basePath}${apiPath}>`;
    },

    // 学習完了時に必ず呼ぶ想定
    $_setScoreResult(scoreResult: STUDY_RESULT): void {
      state.scoreResult = scoreResult;
      state.studyResult = scoreResult;
      state.studyResultTimeout = setTimeout(() => this.clearStudyResult(), 1000);
      // 1問毎、TIMEUPそれぞれで学習時間を取っておきたいのでここで学習時間設定
      this.calcStudySec(scoreResult);
    },

    async $_sendStudyResult(isCorrect: boolean): Promise<void> {
      state.studyRequest.resultNo = isCorrect ? ResultNo.success : ResultNo.fail;
      state.studyRequest.studyStartDt = state.studyStartMoment.format();
      state.studyRequest.courseProgress = this.getProgress();
      await UserApiUtility.getStudyApi().study(state.studyRequest);
    },

    $_isLoaded(): boolean {
      const testText     = '012abcあいう';
      const testCanvas0  = document.createElement('canvas');
      const testCanvas1  = document.createElement('canvas');
      const testContext0 = testCanvas0.getContext('2d');
      const testContext1 = testCanvas1.getContext('2d');
      if (testContext0 === null || testContext1 === null) return false;
      testContext0.font  = 'normal 30px "kyokasyo",sans-serif';
      testContext1.font  = 'normal 30px sans-serif';
      const testMetrix0  = testContext0.measureText(testText); // 読み込みできていない場合はこの描画で取得に行く
      const testMetrix1  = testContext1.measureText(testText);

      return testMetrix0.width !== testMetrix1.width;
    },

  }

});
